import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MODE } from '../../constants/mode'
import { getMode } from '../../hooks/mode'
import { validateKeyboardInput } from '../../utilities/inputValidation'
import CountrySelector from '../CountrySelector'
import CustomCaret, { CustomCaretProps } from '../CustomCaret'
import { FormField } from '../FormField'

const mode = getMode()
const isKioskMode = mode === MODE.KIOSK
const isMobileMode = mode === MODE.MOBILE

interface Props {
  id?: string
  classFormField?: string
  label: string
  sublabel?: string
  isClearNeed?: boolean
  isExtendedClearNeed?: boolean
  clearInputField?: (currentInput: any) => void
  placeholder?: string
  inputRef: HTMLInputElement | null
  httpRef: (el: any) => void
  value: string
  classInput?: string
  onChange: (value: string) => void
  onFocus?: () => void
  onClick?: () => void
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>
  caretRef?: React.RefObject<CustomCaretProps>
  autoFocus?: boolean
  errors?: { formFieldError?: boolean, inputError?: boolean }
  onSelect: (option: string) => void
  setIsCountrySelect: (value: boolean) => void
  isCountrySelect?: boolean | undefined
  setCountry?: any
  countryCode?: string | undefined
  isActive: boolean
  setIsError?: any
}

export const FormFieldCountryInput = ({ httpRef, setIsError, isActive, countryCode, setCountry, isCountrySelect, setIsCountrySelect, id, classFormField, label, isClearNeed, clearInputField, sublabel, inputRef, value, onChange, onFocus, onClick, onKeyDown, caretRef, autoFocus, errors, onSelect }: Props) => {
  const { t } = useTranslation()
  const [errorFormField, setErrorFormField] = useState()
  const cursorPosition = useRef<number>()

  const [, updateState] = useState<any>()
  const forceUpdate = useCallback(() => updateState({}), [])

  const setCursorPosition = (selectionStart: number) => {
    if (!isKioskMode) {
      cursorPosition.current = selectionStart
    }
  }

  const onChangeInput = (value: string) => {    
    const cursorSelectionStart = inputRef ? inputRef.selectionStart || 0 : 0
    setCursorPosition(cursorSelectionStart)

    const isValid = validateKeyboardInput(value, { countries: true })
    if (!isValid) {
      setErrorFormField(t('typeLatinLettersOnly'))
      setCursorPosition(cursorSelectionStart - 1)
      return forceUpdate()
    }

    setErrorFormField(undefined)
    onChange(value)
  }

  const onCleanFormField = (currentInput: HTMLInputElement) => {
    setErrorFormField(undefined)
    if (clearInputField) {
      clearInputField(currentInput)
    }
  }

  useEffect(() => {
    if (!isActive) {
      setErrorFormField(undefined)
    }
  }, [isActive])
  
  useEffect(() => {
    if (isCountrySelect && inputRef) {
      cursorPosition.current = inputRef.value.length
    }
  }, [isCountrySelect])

  useEffect(() => {
    if (!isKioskMode && inputRef && cursorPosition.current !== undefined && isActive) {
      inputRef.setSelectionRange(cursorPosition.current, cursorPosition.current)
    }
  })

  return (
    <FormField
      id={id}
      classFormField={`${isMobileMode ? 'form-field-mobile' : 'form-field'}${classFormField ? ' ' + classFormField : ''}${!isKioskMode ? ' show-cursor' : ''}`}
      label={label}
      isClearNeed={isClearNeed}
      clearInputField={onCleanFormField}
      currentInput={inputRef || null}
      sublabel={errorFormField ? errorFormField : sublabel}
      isError={true}
    >
      <>
        <CountrySelector
          onChange={onChangeInput}
          onSelect={onSelect}
          setCountry={setCountry}
          countryCode={countryCode}
          setIsCountrySelect={setIsCountrySelect}
          isCountrySelect={isCountrySelect}
          value={value}
          httpRef={httpRef}
          onFocus={onFocus ? onFocus : () => { }}
          isActive={isActive}
          autoFocus={!!autoFocus}
          onClick={onClick}
          setIsError={setIsError}
          isError={(errors && errors.inputError)}
          currentInput={inputRef}
          clearInputField={clearInputField}
          onKeyDown={onKeyDown}
        />
        {isKioskMode && caretRef && inputRef && <CustomCaret ref={caretRef} inputRef={inputRef} />}
      </>
    </FormField>
  )
}
